import mixins from 'vue-typed-mixins';
import _ from 'lodash';
import { FieldErrorResponse } from '@codequality/vue-cli-base';

import { E償却項目, E取込データ種別 } from '@/openapi/enum';
import {
  取込Api,
  勘定科目ListItemResponse,
  取込FileListItemResponse,
  取込Pdf固定資産Request,
  帳票ListItemRequest,
} from '@/openapi/user';

import Pdf固定資産台帳TabComponent from '@/components/取込/データ確認/pdf/固定資産台帳/Pdf固定資産台帳Tab.vue';
import C取込Error from '@/components/取込/error/取込Error.vue';

import {
  Pdf固定資産台帳Tab,
  Pdf固定資産台帳Row,
  Pdf固定資産台帳Page,
} from '@/components/取込/データ確認/pdf/固定資産台帳/Pdf固定資産台帳Tab';
import 勘定科目Mixin from '@/entries/user/mixins/勘定科目Mixin';
import 取込Mixin from '@/entries/user/mixins/取込Mixin';
import UserApiUtility from '@/classes/common/UserApiUtility';
import { enableE償却項目s } from '@/components/勘定科目/固定資産カラムSelect/固定資産カラムSelect';

export default mixins(勘定科目Mixin, 取込Mixin).extend({
  name: 'Pdf固定資産台帳Dialog',

  components: {
    Pdf固定資産台帳TabComponent,
    C取込Error,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data(): {
    pdf固定資産台帳Tabs: Pdf固定資産台帳Tab[];
  } {
    return {
      pdf固定資産台帳Tabs: [],
    };
  },

  computed: {
    tabs(): string[] {
      return this.pdf固定資産台帳Tabs.map((tab) => this.get取込データ種別Label(tab.取込データ種別));
    },
    pdf固定資産台帳Tab(): Pdf固定資産台帳Tab | undefined {
      return this.pdf固定資産台帳Tabs[this.tabIndex()];
    },
    filtered勘定科目s(): 勘定科目ListItemResponse[] {
      return this.filter固定資産科目(this.勘定科目s);
    },
  },

  watch: {
    async visible(): Promise<void> {
      if (!this.visible) return;

      // 初期化
      this.setTabIndex(0, 0);
      this.pdf固定資産台帳Tabs = [];
      this.取込データ種別Errors = [];

      // 勘定科目、取込データ取得
      const isError = await this.load取込データs('固定資産台帳');

      // エラーがある場合はダイアログを閉じてreturn
      if (isError) {
        this.close();
        return;
      }

      const 取込データ種別 = this.取込データs[0].取込データ種別!;

      const pages = this.取込データs.map((取込データ) => {
        const list = 取込データ.list ?? [];
        const values = list.length > 0 ? list[0].values : [];

        return {
          rows: list.map((item) => this.$_createPdf固定資産台帳Row(item)),
          selected償却項目s: values.map((_, i) =>
            list.map((r) => r.values[i]).find((value) => enableE償却項目s.find((償却項目) => 償却項目 === value))
          ),
        };
      });

      this.pdf固定資産台帳Tabs = [{ 取込データ種別, pages }];
    },
  },

  methods: {
    close(): void {
      this.$emit('update:visible', false);
    },
    async save(): Promise<void> {
      this.取込データ種別Errors = [];
      const tab = _.cloneDeep(this.pdf固定資産台帳Tabs[0]); // タブは1つのみの想定

      // 項目の選択列で絞り込み
      tab.pages.forEach((page) =>
        page.rows.forEach((row) => {
          row.values = row.values!.filter((_, i) => page.selected償却項目s[i] !== undefined);
        })
      );

      // 対象が1行もない場合はアラートを表示してreturn
      if (tab.pages.every((page) => page.rows.length === 0)) {
        alert('取込対象がありません');
        return;
      }

      // 項目選択の重複チェック
      const 償却項目filter = (page: Pdf固定資産台帳Page) => page.selected償却項目s.filter((s) => s !== undefined);
      for (const page of tab.pages) {
        const filtered項目s = 償却項目filter(page);
        const unique項目s = [...new Set(filtered項目s)];
        if (filtered項目s.length !== unique項目s.length) {
          this.add取込Error(tab.取込データ種別, '選択肢', '同じ項目が選択されています');
          break;
        }
      }

      // 重複チェック不可の場合はここでreturn
      if (this.取込データ種別Errors.length > 0) return;

      // 送信データ成形
      const tsvs = tab.pages.map((page) => {
        // E償却項目に対応した選択項目のインデックス
        const 項目Indexes = Object.values(E償却項目).map((v) => 償却項目filter(page).findIndex((項) => 項 === v));

        return page.rows
          .map((row: Pdf固定資産台帳Row) =>
            Object.values(E償却項目)
              .map((v, i) => {
                switch (v) {
                  case E償却項目.勘定科目Pk:
                    return String(row.勘定科目Pk ?? '');
                  default:
                    const value = row.values[項目Indexes[i]];
                    return value !== undefined ? value : '';
                }
              })
              .join('\t')
          )
          .join('\n');
      });
      const request: 取込Pdf固定資産Request = { tsvs };

      // 送信
      const response = await UserApiUtility.ogApi(取込Api).save取込Pdf固定資産(this.計画書.pk, request);

      const set取込Errors = (errors: FieldErrorResponse[]): void => {
        const pages = this.pdf固定資産台帳Tabs[0].pages; // タブは1つのみの想定
        this.取込データ種別Errors = pages.map((_, index) => ({
          取込データ種別: E取込データ種別.固定資産台帳,
          errors: [],
          page: index + 1,
        }));

        let current = 0;
        const targets = pages.map((page) => {
          const startIndex = current;
          const endIndex = startIndex + page.rows.length;
          current = endIndex;
          return { startIndex, endIndex };
        });

        errors.forEach((error) => {
          const fields = error.field.split('.');
          const rowNumber = Number(fields[0].match(/\d+/));

          const index = targets.findIndex((target) => target.startIndex <= rowNumber && rowNumber < target.endIndex);
          this.取込データ種別Errors[index].errors.push({
            index: rowNumber - targets[index].startIndex,
            field: fields[1],
            message: error.defaultMessage,
          });
        });

        this.取込データ種別Errors = this.取込データ種別Errors.filter((取込Error) => 取込Error.errors.length > 0);
      };
      // サーバー側の入力チェックエラーの場合はエラー表示してreturn
      if (this.is取込ValidationErrorResponse(response, set取込Errors)) return;

      this.$emit('saved');
      this.close();
    },
    output固定資産台帳(): void {
      const createRequest = (tab: Pdf固定資産台帳Tab): 帳票ListItemRequest => {
        const templateRows: number[] = [];
        const values: (string | undefined)[][] = [];
        tab.pages.forEach((page) => {
          templateRows.push(
            this.outputConfig.template.header,
            ...new Array(page.rows.length).fill(this.outputConfig.template.row)
          );
          values.push(
            ['勘定科目の選択', ...page.selected償却項目s],
            ...page.rows.map((row) => [this.勘定科目s.find((勘) => 勘.pk === row.勘定科目Pk)?.c勘定科目, ...row.values])
          );
        });

        const maxLen = Math.max(...tab.pages.map((page) => page.selected償却項目s.length));
        const colIndexes = new Array(maxLen).fill(this.outputConfig.column.value);

        return {
          templateName: '取込固定資産台帳',
          sheetName: this.get取込データ種別Label(tab.取込データ種別),
          templateRows,
          values,
          dynamicConfigs: [{ columns: [this.outputConfig.column.勘定科目, ...colIndexes] }],
        };
      };

      this.outputXlsx(this.pdf固定資産台帳Tabs, createRequest);
    },
    $_createPdf固定資産台帳Row(item: 取込FileListItemResponse): Pdf固定資産台帳Row {
      const 勘定科目 = this.filtered勘定科目s.find((勘) => 勘.c勘定科目 === item.勘定科目);
      return { 勘定科目Pk: 勘定科目?.pk, values: item.values };
    },
  },
});
